/* Third party libraries */
import SummaryStatus from '../../../../components/Rover/Widgets/StatusSummaryWidget';

/* Components */
import Card from '../../../../components/UI/Card';
import { LocationMarkerIcon } from '@heroicons/react/outline';
import SocialDesignationWidget from 'components/Rover/Widgets/SocialDesignationWidget';
import StatCard from 'components/UI/Card/StatCard';
import { CurrencyFormatDisplay } from 'components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import GoogleMap from 'components/UI/GoogleMap';
import { useQueries } from '@tanstack/react-query';
import { getAddressDecode, IGetAddressDecodeResData } from 'api/misc';
import LocationMarker from 'components/UI/GoogleMap/LocationMarker';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ExclamationIcon, XIcon } from '@heroicons/react/solid';
import { Link } from 'react-router-dom';
import { PhoneNumberFormatDisplay } from 'components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import LoadingMapCard from 'components/UI/loading/Dashboard/LoadingMapCard';

export const ChicagoRebuildDashboard = ({ data }: { data: any }) => {
  const {
    data: dashboardData,
    meta: { count },
  } = data;

  const [selectedApplication, setSelectedApplication] = useState<any>();
  const location = useLocation();

  const approvedApplicationCount = dashboardData?.filter(
    (data: any) => data.status === 'APPROVED'
  );

  const totalAmountRequested = dashboardData?.reduce(
    (acc: number, data: any) => {
      const amountRequested =
        data?.applicationData?.financialRequest?.loanRequest?.loanRequestAmount;
      acc = acc + amountRequested;
      return acc;
    },
    0
  );

  const totalAmountApproved = approvedApplicationCount?.reduce(
    (acc: number, data: any) => {
      const amountRequested =
        data?.applicationData?.financialRequest?.loanRequest?.loanRequestAmount;
      acc = acc + amountRequested;
      return acc;
    },
    0
  );

  const summary = {
    APPROVED: {
      status: 'APPROVED',
      _count: { status: 0 },
    },
    SUBMITTED: {
      status: 'SUBMITTED',
      _count: { status: 0 },
    },
    UNDER_REVIEW: {
      status: 'UNDER_REVIEW',
      _count: { status: 0 },
    },
    DOCUMENT_REQUESTED: {
      status: 'DOCUMENT_REQUESTED',
      _count: { status: 0 },
    },
    DENIED: {
      status: 'DENIED',
      _count: { status: 0 },
    },
  };

  const summaryStatus = dashboardData?.reduce?.((acc: any, data: any) => {
    const statusType = data?.status;
    acc[statusType]._count.status++;
    return acc;
  }, summary);

  const socialDesignationArray = dashboardData?.map(
    (data: any) =>
      data?.applicationData?.organizationInformation?.socialDesignation
  );

  let uniqueSocialDesignationArray = [...new Set(socialDesignationArray)];

  const socialDesignationType = uniqueSocialDesignationArray?.reduce?.(
    (acc: any, data: any) => {
      const type = {
        [data]: {
          socialDesignation: data,
          _count: { socialDesignation: 0 },
        },
        ...acc,
      };

      return type;
    },
    {}
  );

  const socialDesignation = dashboardData?.reduce?.((acc: any, data: any) => {
    const designationType =
      data?.applicationData?.organizationInformation?.socialDesignation;
    acc[designationType]._count.socialDesignation++;
    return acc;
  }, socialDesignationType);

  const fetchApplicationsGeoCode = useQueries({
    queries: dashboardData?.map((a: any) => {
      const { businessAddress, businessCity, state, zipCode } =
        a?.applicationData.organizationInformation;
      const fullAddress: string = `${businessAddress}, ${businessCity}, ${state}, ${zipCode}`;

      return {
        queryKey: ['GET_PROJECT_GEO_CODE'],
        queryFn: () => getAddressDecode(fullAddress),
        select: (resData: { data: IGetAddressDecodeResData }) => {
          const { lat, lng } = resData.data;
          const updateAppData = {
            ...a?.applicationData,
            organizationInformation: {
              ...a?.applicationData?.organizationInformation,
              coordinates: { lat, lng },
            },
          };
          return { ...a, ...updateAppData };
        },
      };
    }),
  });
  const applicationsLocationInfo = fetchApplicationsGeoCode
    ?.map((p: any) => p.data)
    .filter((df) => df);

  return (
    <>
      <div className='container mx-auto flex flex-col gap-4'>
        <section className='grid md:grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-8'>
          <StatCard title='Total Applications' stat={count | 0} />
          <StatCard
            title='Approved Applications'
            stat={approvedApplicationCount.length | 0}
          />
          <StatCard
            title='Total Amount Requested'
            stat={<CurrencyFormatDisplay value={totalAmountRequested | 0} />}
          />
          <StatCard
            title='Total Amount Approved'
            stat={<CurrencyFormatDisplay value={totalAmountApproved | 0} />}
          />
        </section>

        <SummaryStatus summary={Object.values(summaryStatus)} />

        <section>
          <Card title='Project Location'>
            {fetchApplicationsGeoCode[0].isFetching && <LoadingMapCard />}
            {!fetchApplicationsGeoCode[0]?.isFetching &&
              dashboardData?.length === 0 && (
                <div className='w-full flex flex-col justify-center items-center h-screen/2'>
                  <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                    <LocationMarkerIcon className='h-8 w-8' />
                    <h1>No Projects</h1>
                  </div>
                  <p>There are no applications to display.</p>
                </div>
              )}

            {!fetchApplicationsGeoCode[0]?.isFetching &&
              applicationsLocationInfo?.length === 0 && (
                <div className='w-full flex flex-col justify-center items-center h-screen/2'>
                  <div className='flex justify-center items-center gap-2 text-tertiaryColor-normal'>
                    <ExclamationIcon className='h-8 w-8' />
                    <h1>Error</h1>
                  </div>
                  <p>
                    {' '}
                    Oops ! There seems to be an issue while trying to geo locate
                    the application business addresses for the map. Please try
                    again later.
                  </p>
                </div>
              )}

            {!fetchApplicationsGeoCode[0]?.isFetching &&
              applicationsLocationInfo[0] && (
                <div className='w-full grid grid-cols-6 h-screen/2'>
                  <GoogleMap
                    defaultCenter={{
                      lat: applicationsLocationInfo[0]?.applicationData
                        ?.organizationInformation?.coordinates.lat,
                      lng: applicationsLocationInfo[0].applicationData
                        ?.organizationInformation?.coordinates.lng,
                    }}
                  >
                    {applicationsLocationInfo?.map((app) => {
                      const appData = app?.applicationData;
                      const selectedAppData =
                        selectedApplication?.applicationData;
                      return (
                        <LocationMarker
                          key={app?.id}
                          lat={
                            appData?.organizationInformation?.coordinates?.lat
                          }
                          lng={
                            appData?.organizationInformation?.coordinates?.lng
                          }
                        >
                          <div className='relative'>
                            {selectedApplication &&
                              selectedApplication?.id === app?.id && (
                                <div
                                  onClick={() =>
                                    setSelectedApplication(undefined)
                                  }
                                  className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                >
                                  <XIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                  <Link
                                    to={`${location.pathname.replace(
                                      'dashboard',
                                      'applications'
                                    )}/${
                                      selectedApplication?.id
                                    }/application_details`}
                                  >
                                    <p className='font-semibold text-primaryColor-normal underline'>
                                      {
                                        selectedAppData?.organizationInformation
                                          ?.businessName
                                      }
                                    </p>
                                  </Link>
                                  <p className='text-xs'>
                                    {
                                      selectedAppData?.organizationName
                                        ?.businessAddress
                                    }
                                  </p>
                                  <p className='text-xs'>
                                    {
                                      selectedAppData?.organizationInformation
                                        ?.businessCity
                                    }
                                    ,{' '}
                                    {
                                      selectedAppData?.organizationInformation
                                        ?.state
                                    }
                                    ,{' '}
                                    {
                                      selectedAppData?.organizationInformation
                                        ?.zipCode
                                    }
                                  </p>
                                  <div className='mt-2 text-sm flex justify-between gap-2'>
                                    <span>Lead Contact</span>
                                    <span className='font-semibold'>
                                      {
                                        selectedAppData?.organizationInformation
                                          ?.leadContact
                                      }
                                    </span>
                                  </div>
                                  <div className='mt-2 text-sm flex justify-between gap-2'>
                                    <span>Email</span>
                                    <span className='font-semibold'>
                                      {
                                        selectedAppData?.organizationInformation
                                          ?.email
                                      }
                                    </span>
                                  </div>
                                  <div className='mt-2 text-sm flex justify-between gap-2'>
                                    <span>Phone</span>
                                    <span className='font-semibold'>
                                      <PhoneNumberFormatDisplay
                                        value={
                                          selectedAppData
                                            ?.organizationInformation?.phone ||
                                          'none'
                                        }
                                      />
                                    </span>
                                  </div>
                                </div>
                              )}
                            {app && (
                              <LocationMarkerIcon
                                onClick={() => setSelectedApplication(app)}
                                className={
                                  selectedApplication?.id === app?.id
                                    ? 'h-8 w-8 text-black'
                                    : 'h-8 w-8 text-red-500 '
                                }
                              />
                            )}
                          </div>
                        </LocationMarker>
                      );
                    })}
                  </GoogleMap>
                </div>
              )}
          </Card>
        </section>
        <section className='grid md:grid-cols-1 xl:grid-cols-5 gap-4 lg:gap-8'>
          <div className='xl:col-span-3'>
            <SocialDesignationWidget data={Object.values(socialDesignation)} />
          </div>
        </section>
      </div>
    </>
  );
};
